// === GENERATED:SCREEN:CLIENT-INFORMATION ===
// Generated by Sluitstraat Generator
// Module: jsonforms
// Step: Client Information (Universal)

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useWorkflow,
  WorkflowPageLayout,
  DateOfBirthField,
  LoadingScreen,
  getNextScreen,
  getPreviousScreen,
  getCurrentStepNumber,
  calculateTotalSteps,
} from '@sluitstraat/core';
import { formatInitials } from '../../utils/formatInitials';
import {
  isValidInitials,
  isValidName,
  isValidEmail,
  isValidPhone,
  isValidDateOfBirth,
  validateClientInfo,
} from './client-information/validation';
import { clientInformationStyles as styles } from '../../styles/commonStyles';
import { SectionIcon } from '../../components/SectionIcon';
import { WithHelpSidebar } from '../../components/HelpCard';
import { WorkflowNavButtons } from '../../components/WorkflowNavButtons';

const SCREEN_ID = 'client-information';

// ============================================
// Validation & Formatting Helper Functions
// (Defined outside component to avoid recreation on each render)
// ============================================


/** Auto-format phone number as user types */
const autoFormatPhoneNumber = (input: string): string => {
  const hasPlus = input.startsWith('+');
  let digits = input.replace(/\D/g, '');

  if (digits.length > 15) {
    digits = digits.slice(0, 15);
  }

  if (hasPlus || digits.startsWith('31')) {
    if (!digits.startsWith('31')) {
      return '+' + digits;
    }
    if (digits.length <= 2) return `+${digits}`;
    if (digits.length <= 3) return `+${digits.slice(0, 2)} ${digits.slice(2)}`;

    if (digits.slice(2, 3) === '6') {
      if (digits.length <= 4) return `+${digits.slice(0, 2)} ${digits.slice(2)}`;
      return `+${digits.slice(0, 2)} ${digits.slice(2, 3)} ${digits.slice(3)}`;
    } else {
      if (digits.length <= 4) return `+${digits.slice(0, 2)} ${digits.slice(2)}`;
      return `+${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4)}`;
    }
  } else if (digits.startsWith('0')) {
    if (digits.length <= 1) return digits;
    if (digits.startsWith('06')) {
      if (digits.length <= 2) return digits;
      return `${digits.slice(0, 2)}-${digits.slice(2, 10)}`;
    } else {
      if (digits.length <= 3) return digits;
      return `${digits.slice(0, 3)}-${digits.slice(3, 10)}`;
    }
  } else if (digits.startsWith('6') && !hasPlus) {
    const formatted = '0' + digits;
    if (formatted.length <= 2) return formatted;
    return `${formatted.slice(0, 2)}-${formatted.slice(2, 10)}`;
  } else {
    return digits;
  }
};

export const ClientInformationScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getStepData, saveStepData, selectedProducts, relationData, setRelationData } = useWorkflow();

  // Initialize with empty defaults
  const defaultData = {
    clientInfo: {
      initials: '',
      lastName: '',
      affix: '',
      gender: '',
      email: '',
      phone: '',
      dateOfBirth: '', // No default - user must enter their actual birthdate
      nationality: 'dutch',
    }
  };

  const [data, setData] = useState<any>(() => {
    const existing = getStepData(SCREEN_ID) || relationData || {};
    // Merge defaults with existing data, existing data takes priority
    return {
      ...defaultData,
      ...existing,
      clientInfo: {
        ...defaultData.clientInfo,
        ...existing.clientInfo
      }
    };
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Validate required data on mount - redirect if missing critical data
  useEffect(() => {
    // Check if at least one product is selected. Generic check (any selected) so new
    // products like doorlopendereis don't need this list updated — previously this was a
    // hardcoded per-product list and silently blocked any product not enumerated here.
    const hasAnyProduct = Object.values(selectedProducts || {}).some((v) => v === true);
    if (!hasAnyProduct) {
      navigate('/workflow/product-selection', { replace: true });
      return;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only guard; re-running on selectedProducts/navigate identity would redirect mid-flow
  }, []); // Run once on mount

  useEffect(() => {
    const loadScreenConfig = async () => {
      try {
        // Verify screen config exists (validates the screen is configured)
        const response = await fetch(`/screens/${SCREEN_ID}.json`);
        if (!response.ok) {
          throw new Error(`Failed to load config for ${SCREEN_ID}`);
        }
        // Config loaded successfully - screen uses custom fields, not JSONForms
        setLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load config');
        setLoading(false);
      }
    };

    loadScreenConfig();
  }, []);

  const handleCustomFieldChange = (field: string, value: any) => {
    const processedValue = value;

    const updatedData = { ...data };

    // Handle nested field paths (e.g., 'clientInfo.dateOfBirth')
    const fieldParts = field.split('.');
    let current = updatedData;

    for (let i = 0; i < fieldParts.length - 1; i++) {
      if (!current[fieldParts[i]]) {
        current[fieldParts[i]] = {};
      }
      current = current[fieldParts[i]];
    }

    current[fieldParts[fieldParts.length - 1]] = processedValue;

    setData(updatedData);
    // Persist the draft on every change: stepper/logo navigation must never drop an edit,
    // and the stepper's forward gate reads the workflow, not this screen's state.
    saveStepData(SCREEN_ID, updatedData);

    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }

    // Sync all clientInfo fields to relationData so products can access them
    if (field.startsWith('clientInfo.')) {
      const fieldName = field.replace('clientInfo.', '');
      setRelationData({
        ...relationData,
        clientInfo: {
          ...relationData?.clientInfo,
          [fieldName]: processedValue
        }
      });
    }
  };

  const handleFieldBlur = (field: string) => {
    // Validate field when user leaves it (on blur)
    const fieldParts = field.split('.');
    let value = data;
    for (const part of fieldParts) {
      value = value?.[part];
    }

    const errors: Record<string, string> = {};
    const isEmpty = !value || (typeof value === 'string' && value.trim().length === 0);

    switch (field) {
      case 'clientInfo.initials':
        if (isEmpty) {
          errors[field] = 'validation.initialsRequired';
        } else if (!isValidInitials(value)) {
          errors[field] = 'validation.initialsInvalid';
        }
        break;
      case 'clientInfo.lastName':
        if (isEmpty) {
          errors[field] = 'validation.lastNameRequired';
        } else if (value.trim().length < 2) {
          errors[field] = 'validation.lastNameTooShort';
        } else if (!isValidName(value)) {
          errors[field] = 'validation.lastNameInvalid';
        }
        break;
      case 'clientInfo.email':
        if (isEmpty) {
          errors[field] = 'validation.emailRequired';
        } else if (!isValidEmail(value)) {
          errors[field] = 'validation.emailInvalid';
        }
        break;
      case 'clientInfo.phone':
        if (isEmpty) {
          errors[field] = 'validation.phoneRequired';
        } else if (!isValidPhone(value)) {
          errors[field] = 'validation.phoneInvalid';
        }
        break;
      case 'clientInfo.gender':
        if (isEmpty) {
          errors[field] = 'validation.genderRequired';
        }
        break;
      case 'clientInfo.dateOfBirth':
        if (isEmpty) {
          errors[field] = 'validation.dateOfBirthRequired';
        } else if (!isValidDateOfBirth(value)) {
          // Check specific reason for better error message
          const dateRegex = /^(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[012])-\d{4}$/;
          if (!dateRegex.test(value)) {
            errors[field] = 'validation.dateOfBirthInvalid';
          } else {
            const parts = value.split('-');
            const year = parseInt(parts[2], 10);
            const date = new Date(year, parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
            const age = new Date().getFullYear() - date.getFullYear();

            if (age < 18) {
              errors[field] = 'validation.dateOfBirthTooYoung';
            } else if (age > 120) {
              errors[field] = 'validation.dateOfBirthTooOld';
            } else {
              errors[field] = 'validation.dateOfBirthInvalid';
            }
          }
        }
        break;
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors((prev) => ({ ...prev, ...errors }));
    } else {
      // Clear error for this field if validation passes
      setValidationErrors((prev) => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  const validateForm = (formData?: any): boolean => {
    const checkData = formData || data;
    const errors = validateClientInfo(checkData?.clientInfo);
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    // Format initials before validation and saving
    const formattedInitials = formatInitials(data?.clientInfo?.initials || '');
    const dataToSave = {
      ...data,
      clientInfo: {
        ...data?.clientInfo,
        initials: formattedInitials
      }
    };
    setData(dataToSave);

    // Validate form before proceeding (pass dataToSave since setData is async)
    if (!validateForm(dataToSave)) {
      // Scroll to top to show errors
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    saveStepData(SCREEN_ID, dataToSave);

    // Save relation data to context
    setRelationData(dataToSave);

    // Use dynamic navigation based on selected products
    const nextScreen = getNextScreen('/workflow/client-information', selectedProducts);
    if (nextScreen) {
      navigate(nextScreen);
    }
  };

  const handlePrevious = () => {
    saveStepData(SCREEN_ID, data);
    const previousScreen = getPreviousScreen('/workflow/client-information', selectedProducts);
    if (previousScreen) {
      navigate(previousScreen);
    }
  };

  if (loading) {
    return <LoadingScreen message={t('common.loadingSchema')} />;
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <h2 style={styles.errorTitle}>{t('common.errorLoadingSchema')}</h2>
        <p>{error}</p>
        <button onClick={() => navigate('/')} style={styles.button}>
          {t('common.backToHome')}
        </button>
      </div>
    );
  }

  // Calculate dynamic step progress
  const currentStep = getCurrentStepNumber('/workflow/client-information', selectedProducts);
  const totalSteps = calculateTotalSteps(selectedProducts);

  return (
    <WorkflowPageLayout currentStep={currentStep} totalSteps={totalSteps}>
      <main style={styles.page}>
        <div style={styles.container}>
          <WithHelpSidebar stackEarly showTrust>
            <header style={styles.contentHeader}>
              <h1 style={styles.title}>{t('screens.clientInfo.title')}</h1>
              <p style={styles.subtitle}>
                {t('screens.clientInfo.subtitle')}
              </p>

              {/* Live visitor indicator */}
            </header>

            <div style={styles.formContainer}>
              {/* Form header with icon */}
              <div style={styles.formHeader}>
                <div style={styles.userIconContainer}>
                  <SectionIcon name="user" />
                </div>
                <div>
                  <h2 style={styles.formTitle}>{t('form.personalInformationTitle')}</h2>
                  <p style={styles.formSubtitle}>
                    {t('common.allFieldsRequired')}
                  </p>
                </div>
              </div>

              {/* Row 1: Name Fields - Initials, Affix, Lastname */}
              <div className="client-info-name-layout">
                <div style={styles.fieldContainer}>
                  <label style={styles.fieldLabel} htmlFor="initials">
                    {t('form.initials')} <span style={styles.required}>*</span>
                  </label>
                  <input
                    id="initials"
                    type="text"
                    value={data?.clientInfo?.initials || ''}
                    onChange={(e) => handleCustomFieldChange('clientInfo.initials', e.target.value)}
                    onBlur={() => {
                      // Format initials on blur (e.g., "tt" -> "T.T.")
                      const formatted = formatInitials(data?.clientInfo?.initials || '');
                      if (formatted !== data?.clientInfo?.initials) {
                        handleCustomFieldChange('clientInfo.initials', formatted);
                      }
                      handleFieldBlur('clientInfo.initials');
                    }}
                    style={{
                      ...styles.fieldInput,
                      ...(validationErrors['clientInfo.initials'] ? styles.fieldInputError : {})
                    }}
                    placeholder={t('form.initialsPlaceholder')}
                    maxLength={20}
                    required
                  />
                  {validationErrors['clientInfo.initials'] && (
                    <p style={styles.errorMessage}>{t(validationErrors['clientInfo.initials'])}</p>
                  )}
                </div>

                <div style={styles.fieldContainer}>
                  <label style={styles.fieldLabel} htmlFor="affix">
                    {t('form.affix')}
                  </label>
                  <input
                    id="affix"
                    type="text"
                    value={data?.clientInfo?.affix || ''}
                    onChange={(e) => handleCustomFieldChange('clientInfo.affix', e.target.value)}
                    style={styles.fieldInput}
                    placeholder={t('form.affixPlaceholder')}
                  />
                </div>

                <div style={styles.fieldContainer}>
                  <label style={styles.fieldLabel} htmlFor="last-name">
                    {t('form.lastName')} <span style={styles.required}>*</span>
                  </label>
                  <input
                    id="last-name"
                    type="text"
                    value={data?.clientInfo?.lastName || ''}
                    onChange={(e) => handleCustomFieldChange('clientInfo.lastName', e.target.value)}
                    onBlur={() => handleFieldBlur('clientInfo.lastName')}
                    style={{
                      ...styles.fieldInput,
                      ...(validationErrors['clientInfo.lastName'] ? styles.fieldInputError : {})
                    }}
                    placeholder={t('form.lastNamePlaceholder')}
                    minLength={2}
                    required
                  />
                  {validationErrors['clientInfo.lastName'] && (
                    <p style={styles.errorMessage}>{t(validationErrors['clientInfo.lastName'])}</p>
                  )}
                </div>
              </div>

              {/* Row 2: Gender, Date of Birth */}
              <div className="client-info-horizontal-layout client-info-gender-row">
                <div style={styles.fieldContainer}>
                  <label style={styles.fieldLabel} htmlFor="gender">
                    {t('form.gender')} <span style={styles.required}>*</span>
                  </label>
                  <select
                    id="gender"
                    value={data?.clientInfo?.gender || ''}
                    onChange={(e) => handleCustomFieldChange('clientInfo.gender', e.target.value)}
                    onBlur={() => handleFieldBlur('clientInfo.gender')}
                    className={validationErrors['clientInfo.gender'] ? 'field-error' : ''}
                    data-error={validationErrors['clientInfo.gender'] ? 'true' : undefined}
                    style={{
                      ...styles.fieldSelect,
                      ...(validationErrors['clientInfo.gender'] ? styles.fieldSelectError : {})
                    }}
                    required
                  >
                    <option value="">{t('form.genderPlaceholder')}</option>
                    <option value="M">{t('form.genderOptions.male')}</option>
                    <option value="V">{t('form.genderOptions.female')}</option>
                  </select>
                  {validationErrors['clientInfo.gender'] && (
                    <p style={styles.errorMessage}>{t(validationErrors['clientInfo.gender'])}</p>
                  )}
                </div>

                <div style={styles.fieldContainer}>
                  <DateOfBirthField
                    value={data?.clientInfo?.dateOfBirth || ''}
                    onChange={(value) => {
                      handleCustomFieldChange('clientInfo.dateOfBirth', value);
                      // Validate after onChange
                      setTimeout(() => handleFieldBlur('clientInfo.dateOfBirth'), 0);
                    }}
                    outputFormat="string"
                    required={true}
                    id="date-of-birth"
                    style={{
                      ...styles.fieldInput,
                      ...(validationErrors['clientInfo.dateOfBirth'] ? styles.fieldInputError : {})
                    }}
                    labelStyle={styles.fieldLabel}
                  />
                  {validationErrors['clientInfo.dateOfBirth'] && (
                    <p style={styles.errorMessage}>{t(validationErrors['clientInfo.dateOfBirth'])}</p>
                  )}
                </div>
              </div>

              {/* Row 3: Phone + Email side by side (same row mechanism and
                  768px stack point as the rows above) */}
              <div className="client-info-horizontal-layout">
                <div style={styles.fieldContainer}>
                  <label style={styles.fieldLabel} htmlFor="phone">
                    {t('form.phone')} <span style={styles.required}>*</span>
                  </label>
                  <div style={styles.phoneInputContainer}>
                    <div style={styles.phoneIconContainer}>
                      <svg style={styles.phoneIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                      </svg>
                    </div>
                    <input
                      id="phone"
                      type="tel"
                      value={data?.clientInfo?.phone || ''}
                      onChange={(e) => {
                        const formatted = autoFormatPhoneNumber(e.target.value);
                        e.target.value = formatted;
                        handleCustomFieldChange('clientInfo.phone', formatted);
                      }}
                      onBlur={() => handleFieldBlur('clientInfo.phone')}
                      style={{
                        ...styles.phoneInput,
                        ...(validationErrors['clientInfo.phone'] ? styles.fieldInputError : {})
                      }}
                      placeholder={t('form.phonePlaceholder')}
                      required
                    />
                  </div>
                  {validationErrors['clientInfo.phone'] && (
                    <p style={styles.errorMessage}>{t(validationErrors['clientInfo.phone'])}</p>
                  )}
                </div>

                <div style={styles.fieldContainer}>
                  <label style={styles.fieldLabel} htmlFor="email">
                    {t('form.email')} <span style={styles.required}>*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={data?.clientInfo?.email || ''}
                    onChange={(e) => handleCustomFieldChange('clientInfo.email', e.target.value)}
                    onBlur={() => handleFieldBlur('clientInfo.email')}
                    style={{
                      ...styles.fieldInput,
                      ...(validationErrors['clientInfo.email'] ? styles.fieldInputError : {})
                    }}
                    placeholder={t('form.emailPlaceholder')}
                    required
                  />
                  {validationErrors['clientInfo.email'] && (
                    <p style={styles.errorMessage}>{t(validationErrors['clientInfo.email'])}</p>
                  )}
                </div>
              </div>
            </div>

            <WorkflowNavButtons onBack={handlePrevious} onNext={handleNext} />
          </WithHelpSidebar>

        </div>
      </main>
    </WorkflowPageLayout>
  );
};

export default ClientInformationScreen;
